'use client';

import type { CSSProperties, ReactNode, UIEventHandler } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  useVirtualizer,
  useWindowVirtualizer,
  type VirtualItem,
  type Virtualizer,
} from '@tanstack/react-virtual';

import { cn } from '../../lib/utils';

export {
  useVirtualizer as useTovVirtualizer,
  useWindowVirtualizer as useTovWindowVirtualizer,
};

export type {
  VirtualItem as TovVirtualItem,
  Virtualizer as TovVirtualizer,
  VirtualizerOptions as TovVirtualizerOptions,
} from '@tanstack/react-virtual';

export interface TovVirtualListRenderItemInput<TItem> {
  item: TItem;
  virtualItem: VirtualItem;
}

export interface TovVirtualGridRenderItemInput<TItem> {
  item: TItem;
  index: number;
  virtualRow: VirtualItem;
}

export interface TovVirtualListProps<TItem> {
  className?: string;
  contentClassName?: string;
  empty?: ReactNode;
  estimateSize: (index: number) => number;
  getKey: (item: TItem, index: number) => string | number;
  height?: number | string;
  items: readonly TItem[];
  onScroll?: UIEventHandler<HTMLDivElement>;
  overscan?: number;
  renderItem: (input: TovVirtualListRenderItemInput<TItem>) => ReactNode;
  role?: string;
  style?: CSSProperties;
}

export interface TovVirtualGridProps<TItem> {
  className?: string;
  contentClassName?: string;
  empty?: ReactNode;
  estimateRowSize: (index: number) => number;
  gap?: number | string;
  getKey: (item: TItem, index: number) => string | number;
  height?: number | string;
  items: readonly TItem[];
  lanes?: number;
  maxLanes?: number;
  minItemWidth?: number;
  onScroll?: UIEventHandler<HTMLDivElement>;
  overscan?: number;
  renderItem: (input: TovVirtualGridRenderItemInput<TItem>) => ReactNode;
  role?: string;
  rowClassName?: string;
  style?: CSSProperties;
}

export function TovVirtualList<TItem>({
  className,
  contentClassName,
  empty = null,
  estimateSize,
  getKey,
  height = 320,
  items,
  onScroll,
  overscan = 6,
  renderItem,
  role,
  style,
}: TovVirtualListProps<TItem>) {
  const parentRef = useRef<HTMLDivElement | null>(null);
  const resolvedHeight = typeof height === 'number' ? `${height}px` : height;
  const itemKeys = useMemo(
    () => items.map((item, index) => getKey(item, index)),
    [getKey, items],
  );
  const virtualizer = useVirtualizer({
    count: items.length,
    estimateSize,
    getItemKey: (index) => itemKeys[index] ?? index,
    getScrollElement: () => parentRef.current,
    overscan,
    useFlushSync: false,
  });

  if (items.length === 0) {
    return <>{empty}</>;
  }

  return (
    <div
      className={cn('tov-virtual-list', className)}
      ref={parentRef}
      onScroll={onScroll}
      role={role}
      style={
        {
          '--tov-virtual-list-height': resolvedHeight,
          ...style,
        } as CSSProperties
      }
    >
      <div
        className={cn('tov-virtual-list__content', contentClassName)}
        style={
          {
            '--tov-virtual-list-total-size': `${virtualizer.getTotalSize()}px`,
          } as CSSProperties
        }
      >
        {virtualizer.getVirtualItems().map((virtualItem) => {
          const item = items[virtualItem.index];

          if (item === undefined) {
            return null;
          }

          return (
            <div
              className="tov-virtual-list__item"
              data-index={virtualItem.index}
              key={virtualItem.key}
              ref={virtualizer.measureElement}
              style={
                {
                  '--tov-virtual-list-item-start': `${virtualItem.start}px`,
                } as CSSProperties
              }
            >
              {renderItem({ item, virtualItem })}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function TovVirtualGrid<TItem>({
  className,
  contentClassName,
  empty = null,
  estimateRowSize,
  gap = '1rem',
  getKey,
  height = 640,
  items,
  lanes,
  maxLanes = 6,
  minItemWidth,
  onScroll,
  overscan = 4,
  renderItem,
  role,
  rowClassName,
  style,
}: TovVirtualGridProps<TItem>) {
  const parentRef = useRef<HTMLDivElement | null>(null);
  const [measuredLaneCount, setMeasuredLaneCount] = useState(1);
  const resolvedHeight = typeof height === 'number' ? `${height}px` : height;
  const resolvedGap = typeof gap === 'number' ? `${gap}px` : gap;
  const resolvedLanes = Math.max(1, Math.floor(lanes ?? measuredLaneCount));
  const rowCount = Math.ceil(items.length / resolvedLanes);

  useEffect(() => {
    if (lanes !== undefined || minItemWidth === undefined) {
      return;
    }

    const measuredMinItemWidth = minItemWidth;
    const element = parentRef.current;
    if (!element) {
      return;
    }

    function updateLaneCount(width: number) {
      setMeasuredLaneCount(
        Math.max(
          1,
          Math.min(maxLanes, Math.floor(width / measuredMinItemWidth)),
        ),
      );
    }

    updateLaneCount(element.getBoundingClientRect().width);

    const resizeObserver = new ResizeObserver((entries) => {
      const entry = entries[0];
      const width = entry?.contentRect.width;

      if (width) {
        updateLaneCount(width);
      }
    });
    resizeObserver.observe(element);

    return () => {
      resizeObserver.disconnect();
    };
  }, [lanes, maxLanes, minItemWidth]);
  const rowKeys = useMemo(
    () =>
      Array.from({ length: rowCount }, (_, rowIndex) =>
        Array.from({ length: resolvedLanes }, (_item, laneIndex) => {
          const itemIndex = rowIndex * resolvedLanes + laneIndex;
          const item = items[itemIndex];

          return item !== undefined ? getKey(item, itemIndex) : '';
        }).join(':'),
      ),
    [getKey, items, resolvedLanes, rowCount],
  );
  const virtualizer = useVirtualizer({
    count: rowCount,
    estimateSize: estimateRowSize,
    getItemKey: (index) => rowKeys[index] ?? index,
    getScrollElement: () => parentRef.current,
    overscan,
    useFlushSync: false,
  });

  if (items.length === 0) {
    return <>{empty}</>;
  }

  return (
    <div
      className={cn('tov-virtual-grid', className)}
      ref={parentRef}
      onScroll={onScroll}
      role={role}
      style={
        {
          '--tov-virtual-grid-height': resolvedHeight,
          '--tov-virtual-grid-gap': resolvedGap,
          '--tov-virtual-grid-lanes': resolvedLanes,
          ...style,
        } as CSSProperties
      }
    >
      <div
        className={cn('tov-virtual-grid__content', contentClassName)}
        style={
          {
            '--tov-virtual-grid-total-size': `${virtualizer.getTotalSize()}px`,
          } as CSSProperties
        }
      >
        {virtualizer.getVirtualItems().map((virtualRow) => {
          const rowItems: Array<{ index: number; item: TItem }> = [];

          for (let laneIndex = 0; laneIndex < resolvedLanes; laneIndex += 1) {
            const index = virtualRow.index * resolvedLanes + laneIndex;
            const item = items[index];

            if (item !== undefined) {
              rowItems.push({ index, item });
            }
          }

          return (
            <div
              className={cn('tov-virtual-grid__row', rowClassName)}
              data-index={virtualRow.index}
              key={virtualRow.key}
              ref={virtualizer.measureElement}
              style={
                {
                  '--tov-virtual-grid-row-start': `${virtualRow.start}px`,
                } as CSSProperties
              }
            >
              {rowItems.map(({ index, item }) => (
                <div
                  className="tov-virtual-grid__item"
                  key={getKey(item, index)}
                >
                  {renderItem({ index, item, virtualRow })}
                </div>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export type TovVirtualListInstance<
  TScrollElement extends Element | Window,
  TItemElement extends Element,
> = Virtualizer<TScrollElement, TItemElement>;
